import {
  CELL,
  CHANCE_COSTS,
  CHANCE_TABLE,
  CREEPS_PER_WAVE,
  DT,
  ENDLESS,
  FLY_ROUTE,
  FLY_WAVES,
  GEM_KEYS,
  GEMS,
  GREATER_SLATES,
  LAST_WAVE,
  NX,
  NY,
  RECIPES,
  SLATE_ANCHOR_TIER,
  SLATE_FODDER_COUNT,
  SLATE_FODDER_TIER,
  SLATE_KEYS,
  SLATE_MIN_DIST,
  SLATE_RECIPES,
  SLATES,
  SPAWN_GAP,
  SPECIALS,
  SPITE_RADIUS,
  START_GOLD,
  START_LIVES,
  SUPERFAST,
  TIER_MULT,
  WAYPOINTS,
  aquaCd,
  poisonDps,
  slowF,
} from '../data/balance.js';
import { Djb2 } from './hash.js';

/**
 * Hash of every value the sim's outcome depends on. Stamped into replays
 * (format v2) so a replay is verifiably bound to the balance it was played
 * under: same hash -> re-simulation is meaningful, different hash -> the
 * balance changed and the replay is historical only.
 */
export function computeBalanceHash(): number {
  const tierSamples = [0, 1, 2, 3, 4, 5];
  const canonical = JSON.stringify({
    board: { NX, NY, CELL, DT },
    run: { START_GOLD, START_LIVES, LAST_WAVE, CREEPS_PER_WAVE, SPAWN_GAP },
    tiers: TIER_MULT,
    chance: { table: CHANCE_TABLE, costs: CHANCE_COSTS },
    waves: {
      fly: [...FLY_WAVES].sort((a, b) => a - b),
      superfast: [...SUPERFAST].sort((a, b) => a - b),
    },
    gems: GEM_KEYS.map((k) => {
      const g = GEMS[k];
      return [k, g.dmg, g.range, g.cd];
    }),
    formulas: tierSamples.map((t) => [slowF(t), poisonDps(t), aquaCd(t)]),
    specials: Object.entries(SPECIALS).map(([k, s]) => [
      k,
      s.dmg,
      s.range,
      s.cd,
      s.multi ?? null,
      s.splash ?? null,
      s.vsAir ?? null,
      s.slow ?? null,
      s.poison ?? null,
      s.aura ?? null,
      s.crit ?? null,
      s.critMult ?? null,
      s.bounty ?? null,
    ]),
    recipes: RECIPES.map((r) => [r.key, ...r.comps.map((c) => `${c[0]}:${c[1]}`)]),
    endless: ENDLESS,
    slates: {
      defs: SLATE_KEYS.map((k) => {
        const s = SLATES[k];
        return [
          k,
          s.radius,
          s.vsAir ?? null,
          s.shred ?? null,
          s.zoneSlow ?? null,
          s.poisonRate ?? null,
          s.rangeBonus ?? null,
          s.auraMult ?? null,
          s.spite ?? null,
          s.dmgTaken ?? null,
        ];
      }),
      recipes: SLATE_RECIPES.map((r) => [r.key, r.anchor]),
      greater: GREATER_SLATES.map((g) => [g.key, ...g.pair]),
      rules: [
        SLATE_ANCHOR_TIER,
        SLATE_FODDER_TIER,
        SLATE_FODDER_COUNT,
        SLATE_MIN_DIST,
        SPITE_RADIUS,
      ],
    },
    waypoints: WAYPOINTS.map((w) => [w.x, w.y]),
    flyRoute: FLY_ROUTE.map((w) => [w.x, w.y]),
  });
  const h = new Djb2();
  h.addString(canonical);
  return h.value;
}
